/**
 * Survey Results Page Component for EEG Survey Integration.
 * Displays survey results with question-by-question analysis and comprehensive AI report.
 * Kept separate from existing codebase.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader, Brain, Sparkles, TrendingUp, AlertCircle, History, Download, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { surveyService } from '../../services/surveyService';
import jsPDF from 'jspdf';
import FullSurveyAnswerGraph from '../../components/survey/FullSurveyAnswerGraph';
import './SurveyResults.css';

const SurveyResults = () => {
  const { responseId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [eegReadings, setEegReadings] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionInterpretations, setQuestionInterpretations] = useState({});
  const [questionSincerityAnalysis, setQuestionSincerityAnalysis] = useState({});
  const [loadingInterpretation, setLoadingInterpretation] = useState({});
  const [comprehensiveReport, setComprehensiveReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previousChartData, setPreviousChartData] = useState(null);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);

  // Get normalization preference for this response (default: true)
  const getNormalizationPreference = () => {
    try {
      const key = `normalization_${responseId}`;
      const stored = localStorage.getItem(key);
      return stored !== null ? stored === 'true' : true; // Default to true
    } catch {
      return true;
    }
  };

  useEffect(() => {
    loadSurveyData();
  }, [responseId]);

  const loadSurveyData = async () => {
    try {
      setLoading(true);
      
      // Load response with answers
      const responseData = await surveyService.getResponse(responseId);
      setResponse(responseData);
      setAnswers(responseData.answers || []);
      
      // Load stored comprehensive report if it exists and is valid
      if (responseData.comprehensive_report) {
        // Check if it's a valid report (not just an error message)
        const report = responseData.comprehensive_report;
        const errorMessages = [
          'Unable to generate report at this time.',
          'Unable to generate interpretation at this time.'
        ];
        if (report.report && !errorMessages.includes(report.report) && report.report.trim().length > 50) {
          setComprehensiveReport(report);
        } else {
          // Report exists but is invalid, clear it so user can regenerate
          console.log('[SurveyResults] Stored report is invalid, allowing regeneration');
          setComprehensiveReport(null);
        }
      }
      
      // Load stored interpretations from answers
      const storedInterpretations = {};
      if (responseData.answers) {
        responseData.answers.forEach(answer => {
          if (answer.ai_interpretation) {
            const questionId = answer.question?.id || answer.question_id;
            if (questionId) {
              storedInterpretations[questionId] = answer.ai_interpretation;
            }
          }
        });
        setQuestionInterpretations(storedInterpretations);
      }
      
      // Load EEG readings with normalization preference
      const enableNormalization = getNormalizationPreference();
      const readingsData = await surveyService.getEEGReadings(responseId, 1, 10000, enableNormalization);
      setEegReadings(readingsData.readings || []);
      
    } catch (error) {
      console.error('Error loading survey data:', error);
      toast.error('Failed to load survey results');
    } finally {
      setLoading(false);
    }
  };

  const getQuestionReadings = (questionId) => {
    if (!questionId) return [];
    return eegReadings.filter(r => {
      const rQuestionId = r.question_id || r.question?.id || r.question;
      return rQuestionId === questionId || rQuestionId === String(questionId);
    });
  };

  const getQuestionInterpretation = useCallback(async (questionId) => {
    if (questionInterpretations[questionId] || loadingInterpretation[questionId]) {
      return; // Already loaded or loading
    }

    try {
      setLoadingInterpretation(prev => ({ ...prev, [questionId]: true }));
      const enableNormalization = getNormalizationPreference();
      const result = await surveyService.getQuestionInterpretation(responseId, questionId, enableNormalization);
      setQuestionInterpretations(prev => ({
        ...prev,
        [questionId]: result.interpretation
      }));
      // Store sincerity analysis separately
      if (result.sincerity_analysis) {
        setQuestionSincerityAnalysis(prev => ({
          ...prev,
          [questionId]: result.sincerity_analysis
        }));
      }
    } catch (error) {
      console.error('Error loading interpretation:', error);
      toast.error('Failed to load interpretation');
    } finally {
      setLoadingInterpretation(prev => ({ ...prev, [questionId]: false }));
    }
  }, [responseId, questionInterpretations, loadingInterpretation]);

  // Check if comprehensive report is valid (not just an error message)
  const isReportValid = (report) => {
    if (!report) return false;
    // Check if report is just an error message
    const errorMessages = [
      'Unable to generate report at this time.',
      'Unable to generate interpretation at this time.'
    ];
    if (errorMessages.includes(report.report)) {
      return false;
    }
    // Check if report has meaningful content
    return report.report && report.report.trim().length > 50;
  };

  const handleRegenerateClick = () => {
    setShowRegenerateModal(true);
  };

  const handleRegenerateConfirm = async () => {
    setShowRegenerateModal(false);
    await generateComprehensiveReport(true);
  };

  const handleRegenerateCancel = () => {
    setShowRegenerateModal(false);
  };

  const generateComprehensiveReport = async (forceRegenerate = false) => {
    try {
      setLoadingReport(true);
      const enableNormalization = getNormalizationPreference();
      console.log('[SurveyResults] Generating comprehensive report for response:', responseId, 'forceRegenerate:', forceRegenerate, 'normalization:', enableNormalization);
      const report = await surveyService.generateComprehensiveReport(responseId, forceRegenerate, enableNormalization);
      
      // Safely log report structure without circular references
      console.log('[SurveyResults] Received report keys:', report ? Object.keys(report) : 'null');
      if (report && report.anchor_scores) {
        console.log('[SurveyResults] Anchor scores available:', Object.keys(report.anchor_scores));
      }
      if (report && report.graph_data) {
        console.log('[SurveyResults] Graph data available:', report.graph_data.length, 'points');
      }
      
      // Check if response is an error
      if (report && report.detail) {
        console.error('[SurveyResults] API returned error:', report.detail);
        toast.error(report.detail || 'Failed to generate comprehensive report');
        return;
      }
      
      const isValid = isReportValid(report);
      console.log('[SurveyResults] Report validity check:', isValid);
      setComprehensiveReport(report);
      if (isValid) {
        toast.success('Comprehensive report generated!');
      } else {
        console.warn('[SurveyResults] Report is invalid');
        toast.error('Failed to generate comprehensive report. Please try again.');
      }
    } catch (error) {
      console.error('[SurveyResults] Error generating report:', error.message);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate comprehensive report';
      toast.error(errorMessage);
    } finally {
      setLoadingReport(false);
    }
  };

  const downloadPDF = () => {
    if (!comprehensiveReport) {
      toast.error('No report available to download. Please generate a report first.');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - 2 * margin;
      const lineHeight = 7; // Line height in points
      const sectionSpacing = 12; // Spacing between sections
      const bottomMargin = 30; // Bottom margin to prevent overflow
      let yPosition = margin;

      // Helper function to check if we need a new page
      const checkPageBreak = (requiredHeight) => {
        if (yPosition + requiredHeight > pageHeight - bottomMargin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Helper function to add text with word wrapping and proper page breaks
      const addWrappedText = (text, x, y, maxWidth, fontSize = 11, lineSpacing = lineHeight) => {
        if (!text || text.trim() === '') return 0;
        
        doc.setFontSize(fontSize);
        const lines = doc.splitTextToSize(String(text), maxWidth);
        let currentY = y;
        let totalHeight = 0;

        lines.forEach((line, index) => {
          // Check if we need a new page before adding this line
          if (currentY + lineSpacing > pageHeight - bottomMargin) {
            doc.addPage();
            currentY = margin;
            totalHeight = 0; // Reset height on new page
          }
          
          doc.text(line, x, currentY);
          currentY += lineSpacing;
          totalHeight += lineSpacing;
        });

        return totalHeight;
      };

      // Helper function to add a section heading
      const addSectionHeading = (text, fontSize = 14) => {
        checkPageBreak(15); // Reserve space for heading
        doc.setFontSize(fontSize);
        doc.setFont(undefined, 'bold');
        doc.text(text, margin, yPosition);
        yPosition += 10;
      };

      // Title
      checkPageBreak(25);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      const surveyTitle = response?.survey?.title || 'Resilience Prediction Survey';
      doc.text(surveyTitle, margin, yPosition);
      yPosition += 10;

      // Date
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const reportDate = response?.comprehensive_report_generated_at 
        ? new Date(response.comprehensive_report_generated_at).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })
        : new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          });
      doc.text(`Generated on: ${reportDate}`, margin, yPosition);
      yPosition += sectionSpacing;

      // Overall Analysis
      if (comprehensiveReport.report || comprehensiveReport.overall_interpretation) {
        addSectionHeading('Overall Analysis');
        
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        const overallText = comprehensiveReport.report || comprehensiveReport.overall_interpretation || '';
        const overallHeight = addWrappedText(overallText, margin, yPosition, maxWidth, 11, lineHeight);
        yPosition += overallHeight + sectionSpacing;
      }

      // Key Highlights
      if (comprehensiveReport.highlights && comprehensiveReport.highlights.length > 0) {
        addSectionHeading('Key Highlights');
        
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        comprehensiveReport.highlights.forEach((highlight, idx) => {
          if (!highlight) return;
          const bulletText = `• ${String(highlight)}`;
          const highlightHeight = addWrappedText(bulletText, margin + 5, yPosition, maxWidth - 5, 11, lineHeight);
          yPosition += highlightHeight + 3; // Smaller spacing between list items
        });
        yPosition += sectionSpacing - 3; // Adjust for extra spacing
      }

      // Resilience Profile Interpretation
      if (comprehensiveReport.interpretation) {
        addSectionHeading('Resilience Profile Interpretation');
        
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        const interpretationHeight = addWrappedText(
          String(comprehensiveReport.interpretation), 
          margin, 
          yPosition, 
          maxWidth, 
          11, 
          lineHeight
        );
        yPosition += interpretationHeight + sectionSpacing;
      }

      // Areas for Improvement
      if (comprehensiveReport.areas_to_improve && comprehensiveReport.areas_to_improve.length > 0) {
        addSectionHeading('Areas for Improvement');
        
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        comprehensiveReport.areas_to_improve.forEach((area, idx) => {
          if (!area) return;
          const bulletText = Array.isArray(area) ? area.join(' ') : String(area);
          const areaText = `• ${bulletText}`;
          const areaHeight = addWrappedText(areaText, margin + 5, yPosition, maxWidth - 5, 11, lineHeight);
          yPosition += areaHeight + 3; // Smaller spacing between list items
        });
      }

      // Generate filename
      const filename = `Resilience_Survey_Report_${responseId}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Save the PDF
      doc.save(filename);
      toast.success('PDF report downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF report');
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0 && !isTransitioning) {
      // Store current chart data for transition
      const currentQId = answers[currentQuestionIndex]?.question?.id || answers[currentQuestionIndex]?.question_id;
      if (currentQId) {
        const currentReadings = getQuestionReadings(currentQId);
        setPreviousChartData(currentReadings.map((reading, index) => ({
          index,
          delta: reading.delta || 0,
          theta: reading.theta || 0,
          alpha: reading.alpha || 0,
          beta: reading.beta || 0,
          gamma: reading.gamma || 0,
        })));
      }
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
        setTimeout(() => {
          setIsTransitioning(false);
          setPreviousChartData(null);
        }, 400);
      }, 50);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < answers.length - 1 && !isTransitioning) {
      // Store current chart data for transition
      const currentQId = answers[currentQuestionIndex]?.question?.id || answers[currentQuestionIndex]?.question_id;
      if (currentQId) {
        const currentReadings = getQuestionReadings(currentQId);
        setPreviousChartData(currentReadings.map((reading, index) => ({
          index,
          delta: reading.delta || 0,
          theta: reading.theta || 0,
          alpha: reading.alpha || 0,
          beta: reading.beta || 0,
          gamma: reading.gamma || 0,
        })));
      }
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setTimeout(() => {
          setIsTransitioning(false);
          setPreviousChartData(null);
        }, 400);
      }, 50);
    }
  };

  const handleQuestionClick = (questionId) => {
    console.log('[SurveyResults] handleQuestionClick called with questionId:', questionId);
    // Find the index of the question in answers array
    const questionIndex = answers.findIndex(
      answer => {
        const answerQuestionId = answer.question?.id || answer.question_id;
        return String(answerQuestionId) === String(questionId);
      }
    );
    
    console.log('[SurveyResults] Found question index:', questionIndex, 'Current index:', currentQuestionIndex);
    
    if (questionIndex !== -1 && questionIndex !== currentQuestionIndex) {
      setCurrentQuestionIndex(questionIndex);
    }
  };

  // Load interpretation when question changes
  useEffect(() => {
    if (answers.length > 0 && currentQuestionIndex < answers.length) {
      const currentQuestion = answers[currentQuestionIndex];
      if (currentQuestion) {
        const questionId = currentQuestion.question?.id || currentQuestion.question_id;
        if (questionId) {
          getQuestionInterpretation(questionId);
        }
      }
    }
  }, [currentQuestionIndex, answers, getQuestionInterpretation]);

  if (loading) {
    return (
      <div className="survey-results-page">
        <div className="survey-loading">
          <Loader className="spinner" />
          <p>Loading survey results...</p>
        </div>
      </div>
    );
  }

  if (!response || answers.length === 0) {
    return (
      <div className="survey-results-page">
        <div className="survey-error">
          <AlertCircle />
          <p>No survey results found</p>
          <button onClick={() => navigate('/survey')} className="btn-primary">
            Back to Survey
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = answers[currentQuestionIndex];
  const currentQuestionId = currentQuestion ? (currentQuestion.question?.id || currentQuestion.question_id) : null;
  const questionReadings = currentQuestionId ? getQuestionReadings(currentQuestionId) : [];
  const interpretation = currentQuestionId ? questionInterpretations[currentQuestionId] : null;
  const sincerityAnalysis = currentQuestionId ? questionSincerityAnalysis[currentQuestionId] : null;
  const isLoadingInterpretation = currentQuestionId ? loadingInterpretation[currentQuestionId] : false;

  // Prepare chart data
  const chartData = questionReadings.map((reading, index) => ({
    index,
    delta: reading.delta || 0,
    theta: reading.theta || 0,
    alpha: reading.alpha || 0,
    beta: reading.beta || 0,
    gamma: reading.gamma || 0,
    phase: reading.phase,
    timestamp: reading.timestamp
  }));

  // Find the index where the answer was submitted
  const answerSubmittedAt = currentQuestion?.answer_submitted_at;
  let answerSubmissionIndex = null;
  if (answerSubmittedAt && chartData.length > 0) {
    const answerTime = new Date(answerSubmittedAt).getTime();
    // Find the closest reading to the answer submission time
    let minDiff = Infinity;
    chartData.forEach((dataPoint, index) => {
      if (dataPoint.timestamp) {
        const readingTime = new Date(dataPoint.timestamp).getTime();
        const diff = Math.abs(readingTime - answerTime);
        if (diff < minDiff) {
          minDiff = diff;
          answerSubmissionIndex = index;
        }
      }
    });
  }

  return (
    <div className="survey-results-page">
      <div className="survey-results-container">
        {/* Header */}
        <div className="results-header">
          <button
            className="btn-back-to-list"
            onClick={() => navigate('/survey/history')}
            title="Back to Surveys List"
          >
            <ArrowLeft size={18} />
            Back to Surveys List
          </button>
          <div className="results-header-content">
            <h1>Survey Results</h1>
            <p className="results-subtitle">{response.survey?.title || 'Resilience Prediction Survey'}</p>
            <p className="results-meta">
              Completed: {new Date(response.completed_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Section 1: Question-by-Question Analysis */}
        <div className="results-section question-analysis-section">
          <h2 className="section-title">
            <Brain size={24} />
            Question-by-Question Analysis
          </h2>

          {currentQuestion && (
            <div className="question-analysis-card">
              {/* Navigation */}
              <div className="question-navigation">
                <button
                  className="btn-nav btn-prev"
                  onClick={handlePrevious}
                  disabled={currentQuestionIndex === 0}
                >
                  <ChevronLeft size={20} />
                  Previous
                </button>
                
                <div className="question-counter">
                  Question {currentQuestionIndex + 1} of {answers.length}
                </div>
                
                <button
                  className="btn-nav btn-next"
                  onClick={handleNext}
                  disabled={currentQuestionIndex === answers.length - 1}
                >
                  Next
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Question and Answer */}
              <div className="question-display">
                <h3 className="question-text">{currentQuestion.question?.question_text || 'Question'}</h3>
                <div className="answer-display">
                  <span className="answer-label">Your Answer:</span>
                  <span className="answer-value">{currentQuestion.answer_text}/10</span>
                </div>
              </div>

              {/* EEG Chart */}
              {questionReadings.length > 0 && (
                <div className="eeg-chart-container">
                  <h4 className="chart-title">EEG Frequency Bands Over Time</h4>
                  <EEGChart 
                    data={chartData} 
                    answerSubmissionIndex={answerSubmissionIndex}
                    previousData={previousChartData}
                    isTransitioning={isTransitioning}
                  />
                </div>
              )}

              {/* AI Interpretation */}
              <div className="interpretation-section">
                <h4 className="interpretation-title">
                  <Sparkles size={18} />
                  AI Interpretation
                </h4>
                {isLoadingInterpretation ? (
                  <div className="interpretation-loading">
                    <Loader className="spinner-small" />
                    <span>Generating interpretation...</span>
                  </div>
                ) : interpretation ? (
                  <div className="interpretation-text">{interpretation}</div>
                ) : (
                  <div className="interpretation-placeholder">
                    Click to generate AI interpretation for this question
                  </div>
                )}
              </div>

              {/* Sincerity Analysis */}
              {sincerityAnalysis && sincerityAnalysis.sincerity_score !== undefined && (
                <div className="sincerity-section">
                  <h4 className="sincerity-title">
                    <Brain size={18} />
                    Engagement & Sincerity Analysis
                  </h4>
                  <div className="sincerity-content">
                    <div className="sincerity-assessment">
                      <span className="sincerity-label">Sincerity Assessment:</span>
                      <span className={`sincerity-status ${sincerityAnalysis.is_sincere ? 'sincere' : 'less-sincere'}`}>
                        {sincerityAnalysis.is_sincere ? 'SINCERE' : 'LESS SINCERE'}
                      </span>
                      <span className="sincerity-classification">
                        ({sincerityAnalysis.sincerity_assessment?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'})
                      </span>
                    </div>
                    {sincerityAnalysis.correlation_coefficient !== undefined && (
                      <div className="sincerity-correlation">
                        <span className="sincerity-label">Alpha-Beta vs Delta-Theta Correlation:</span>
                        <span className="sincerity-value">{sincerityAnalysis.correlation_coefficient.toFixed(3)}</span>
                        <span className="sincerity-note">
                          {sincerityAnalysis.correlation_coefficient < -0.2 
                            ? '(Negative - indicates active engagement when answering)' 
                            : sincerityAnalysis.correlation_coefficient > 0.2 
                            ? '(Positive - suggests passive/unfocused state)' 
                            : '(Near zero - mixed pattern)'}
                        </span>
                      </div>
                    )}
                    <div className="sincerity-score">
                      <span className="sincerity-label">Engagement Score:</span>
                      <span className="sincerity-value">{sincerityAnalysis.sincerity_score.toFixed(1)}%</span>
                    </div>
                    {sincerityAnalysis.alpha_beta_avg !== undefined && (
                      <div className="sincerity-details">
                        <div className="sincerity-detail-item">
                          <span>Active Engagement (Alpha + Beta):</span>
                          <span>{sincerityAnalysis.alpha_beta_avg.toFixed(1)}%</span>
                        </div>
                        <div className="sincerity-detail-item">
                          <span>Passive States (Delta + Theta):</span>
                          <span>{sincerityAnalysis.delta_theta_avg.toFixed(1)}%</span>
                        </div>
                        {sincerityAnalysis.ratio !== undefined && (
                          <div className="sincerity-detail-item">
                            <span>Active/Passive Ratio:</span>
                            <span>{sincerityAnalysis.ratio.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Section 2: Comprehensive Report */}
        <div className="results-section comprehensive-report-section">
          <h2 className="section-title">
            <TrendingUp size={24} />
            Comprehensive Survey Analysis
          </h2>

          {!comprehensiveReport || !isReportValid(comprehensiveReport) ? (
            <div className="report-generate-card">
              <p>Generate a comprehensive AI-powered analysis of your entire survey, including:</p>
              <ul>
                <li>Overall resilience profile interpretation</li>
                <li>Key highlights and patterns</li>
                <li>Areas for improvement with actionable recommendations</li>
              </ul>
              <button
                className="btn-generate-report"
                onClick={() => generateComprehensiveReport(false)}
                disabled={loadingReport}
              >
                {loadingReport ? (
                  <>
                    <Loader className="spinner-small" />
                    Generating Report...
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    Generate Comprehensive Report
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="comprehensive-report-card">
              {/* Report Content */}
              <div className="report-section">
                <h3>Overall Analysis</h3>
                <div className="report-text">{comprehensiveReport.report}</div>
              </div>

              {comprehensiveReport.highlights && comprehensiveReport.highlights.length > 0 && (
                <div className="report-section">
                  <h3>Key Highlights</h3>
                  <ul className="highlights-list">
                    {comprehensiveReport.highlights.map((highlight, idx) => (
                      <li key={idx}>{highlight}</li>
                    ))}
                  </ul>
                </div>
              )}

              {comprehensiveReport.interpretation && (
                <div className="report-section">
                  <h3>Resilience Profile Interpretation</h3>
                  <div className="report-text">{comprehensiveReport.interpretation}</div>
                </div>
              )}

              {/* Survey-Wide Sincerity Analysis */}
              {comprehensiveReport.survey_sincerity_analysis && comprehensiveReport.survey_sincerity_analysis.sincerity_score !== undefined && (
                <div className="report-section sincerity-section survey-wide-sincerity">
                  <h3 className="sincerity-title">
                    <Brain size={20} />
                    Survey-Wide Engagement & Sincerity Assessment
                  </h3>
                  <div className="sincerity-content">
                    <div className="sincerity-assessment-display">
                      <span className="sincerity-label">Overall Assessment:</span>
                      <span className={`sincerity-status ${comprehensiveReport.survey_sincerity_analysis.is_sincere ? 'sincere' : 'less-sincere'}`}>
                        {comprehensiveReport.survey_sincerity_analysis.is_sincere ? 'SINCERE' : 'LESS SINCERE'}
                      </span>
                      <span className="sincerity-classification">
                        ({comprehensiveReport.survey_sincerity_analysis.sincerity_assessment?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'})
                      </span>
                    </div>
                    
                    {comprehensiveReport.survey_sincerity_analysis.correlation_coefficient !== undefined && (
                      <div className="sincerity-correlation">
                        <span className="sincerity-label">Alpha-Beta vs Delta-Theta Correlation:</span>
                        <span className="sincerity-value">{comprehensiveReport.survey_sincerity_analysis.correlation_coefficient.toFixed(3)}</span>
                        <span className="sincerity-note">
                          {comprehensiveReport.survey_sincerity_analysis.correlation_coefficient < -0.2 
                            ? '(Negative correlation indicates active engagement throughout the survey)' 
                            : comprehensiveReport.survey_sincerity_analysis.correlation_coefficient > 0.2 
                            ? '(Positive correlation suggests passive/unfocused state)' 
                            : '(Near zero correlation indicates mixed engagement pattern)'}
                        </span>
                      </div>
                    )}
                    
                    <div className="sincerity-score">
                      <span className="sincerity-label">Overall Engagement Score:</span>
                      <span className="sincerity-value">{comprehensiveReport.survey_sincerity_analysis.sincerity_score.toFixed(1)}%</span>
                    </div>
                    
                    {comprehensiveReport.survey_sincerity_analysis.alpha_beta_avg !== undefined && (
                      <div className="sincerity-details">
                        <div className="sincerity-detail-item">
                          <span>Average Alpha-Beta Activity:</span>
                          <span>{comprehensiveReport.survey_sincerity_analysis.alpha_beta_avg.toFixed(1)}%</span>
                        </div>
                        <div className="sincerity-detail-item">
                          <span>Average Delta-Theta Activity:</span>
                          <span>{comprehensiveReport.survey_sincerity_analysis.delta_theta_avg.toFixed(1)}%</span>
                        </div>
                        {comprehensiveReport.survey_sincerity_analysis.ratio !== undefined && (
                          <div className="sincerity-detail-item">
                            <span>Alpha-Beta / Delta-Theta Ratio:</span>
                            <span>{comprehensiveReport.survey_sincerity_analysis.ratio.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Engagement Consistency Analysis (ChatGPT's Recommended Approach) */}
              {comprehensiveReport.engagement_consistency_analysis && comprehensiveReport.engagement_consistency_analysis.engagement_consistency_score !== undefined && (
                <div className="report-section engagement-consistency-section">
                  <h3 className="engagement-consistency-title">
                    <TrendingUp size={20} />
                    Cognitive Engagement Consistency Index
                  </h3>
                  <div className="engagement-consistency-content">
                    <div className="consistency-assessment-display">
                      <span className="consistency-label">Consistency Assessment:</span>
                      <span className={`consistency-status ${comprehensiveReport.engagement_consistency_analysis.engagement_stability === 'high' ? 'high-consistency' : comprehensiveReport.engagement_consistency_analysis.engagement_stability === 'moderate' ? 'moderate-consistency' : comprehensiveReport.engagement_consistency_analysis.engagement_stability === 'low' ? 'low-consistency' : 'variable-consistency'}`}>
                        {comprehensiveReport.engagement_consistency_analysis.engagement_stability?.toUpperCase() || 'UNKNOWN'}
                      </span>
                      <span className="consistency-classification">
                        ({comprehensiveReport.engagement_consistency_analysis.consistency_assessment?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'})
                      </span>
                    </div>
                    
                    <div className="consistency-score">
                      <span className="consistency-label">Engagement Consistency Score:</span>
                      <span className="consistency-value">{comprehensiveReport.engagement_consistency_analysis.engagement_consistency_score.toFixed(1)}/100</span>
                    </div>
                    
                    <div className="consistency-stability">
                      <span className="consistency-label">Variance Stability:</span>
                      <span className="consistency-value">{comprehensiveReport.engagement_consistency_analysis.variance_stability.toFixed(1)}%</span>
                      <span className="consistency-note">
                        (Lower variance indicates more consistent cognitive engagement throughout the survey)
                      </span>
                    </div>
                    
                    {comprehensiveReport.engagement_consistency_analysis.avg_beta_alpha_ratio !== undefined && (
                      <div className="consistency-details">
                        <div className="consistency-detail-item">
                          <span>Average Beta/Alpha Ratio:</span>
                          <span>{comprehensiveReport.engagement_consistency_analysis.avg_beta_alpha_ratio.toFixed(3)}</span>
                          <span className="consistency-detail-note">(Higher = more external task engagement)</span>
                        </div>
                        <div className="consistency-detail-item">
                          <span>Average Beta/Theta Ratio:</span>
                          <span>{comprehensiveReport.engagement_consistency_analysis.avg_beta_theta_ratio.toFixed(3)}</span>
                          <span className="consistency-detail-note">(Higher = more cognitive activation vs mental effort)</span>
                        </div>
                        {comprehensiveReport.engagement_consistency_analysis.std_beta_alpha_ratio !== undefined && (
                          <>
                            <div className="consistency-detail-item">
                              <span>Beta/Alpha Ratio Std Dev:</span>
                              <span>{comprehensiveReport.engagement_consistency_analysis.std_beta_alpha_ratio.toFixed(3)}</span>
                              <span className="consistency-detail-note">(Lower = more stable engagement)</span>
                            </div>
                            <div className="consistency-detail-item">
                              <span>Beta/Theta Ratio Std Dev:</span>
                              <span>{comprehensiveReport.engagement_consistency_analysis.std_beta_theta_ratio.toFixed(3)}</span>
                              <span className="consistency-detail-note">(Lower = more stable cognitive state)</span>
                            </div>
                          </>
                        )}
                        {comprehensiveReport.engagement_consistency_analysis.coefficient_of_variation_beta_alpha !== undefined && (
                          <>
                            <div className="consistency-detail-item">
                              <span>Coefficient of Variation (Beta/Alpha):</span>
                              <span>{comprehensiveReport.engagement_consistency_analysis.coefficient_of_variation_beta_alpha.toFixed(1)}%</span>
                              <span className="consistency-detail-note">(Lower CV = more consistent)</span>
                            </div>
                            <div className="consistency-detail-item">
                              <span>Coefficient of Variation (Beta/Theta):</span>
                              <span>{comprehensiveReport.engagement_consistency_analysis.coefficient_of_variation_beta_theta.toFixed(1)}%</span>
                              <span className="consistency-detail-note">(Lower CV = more consistent)</span>
                            </div>
                          </>
                        )}
                        {comprehensiveReport.engagement_consistency_analysis.baseline_adjusted_beta_alpha !== undefined && (
                          <div className="consistency-detail-item">
                            <span>Baseline-Adjusted Beta/Alpha:</span>
                            <span>{comprehensiveReport.engagement_consistency_analysis.baseline_adjusted_beta_alpha.toFixed(3)}</span>
                            <span className="consistency-detail-note">(Normalized to individual baseline)</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="consistency-explanation">
                      <p className="consistency-explanation-text">
                        <strong>What this measures:</strong> This index assesses the consistency of cognitive engagement throughout the survey using ratio-based metrics (Beta/Alpha, Beta/Theta) and variance stability. Higher scores indicate more stable, sustained engagement. This is distinct from sincerity assessment and focuses on engagement consistency patterns.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {comprehensiveReport.areas_to_improve && comprehensiveReport.areas_to_improve.length > 0 && (
                <div className="report-section">
                  <h3>Areas for Improvement</h3>
                  <ul className="improvements-list">
                    {comprehensiveReport.areas_to_improve.map((area, idx) => (
                      <li key={idx}>{area}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Full Survey Answer Graph */}
              {comprehensiveReport.graph_data && comprehensiveReport.graph_data.length > 0 && (
                <div className="report-section">
                  <FullSurveyAnswerGraph 
                    graphData={comprehensiveReport.graph_data}
                    anchorScores={comprehensiveReport.anchor_scores}
                    baseline={comprehensiveReport.baseline}
                    onQuestionClick={handleQuestionClick}
                  />
                </div>
              )}

              {/* Graph Analysis */}
              {comprehensiveReport.graph_analysis && (
                <div className="report-section">
                  <h3>Answer Pattern Analysis</h3>
                  <div className="report-text">{comprehensiveReport.graph_analysis}</div>
                </div>
              )}

              <div className="report-actions">
                {/* PDF download button hidden - PDF export not working properly */}
                {/* <button
                  className="btn-download-pdf"
                  onClick={downloadPDF}
                  disabled={!comprehensiveReport}
                >
                  <Download size={18} />
                  Download PDF
                </button> */}
                <button
                  className="btn-regenerate"
                  onClick={handleRegenerateClick}
                  disabled={loadingReport}
                >
                  {loadingReport ? (
                    <>
                      <Loader className="spinner-small" />
                      Regenerating...
                    </>
                  ) : (
                    'Regenerate Report'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Navigation Section */}
          <div className="results-navigation-section">
            <button
              className="btn-secondary"
              onClick={() => navigate('/survey/history')}
            >
              <History size={18} /> View All Surveys
            </button>
            <button
              className="btn-secondary"
              onClick={() => navigate('/survey')}
            >
              Start New Survey
            </button>
          </div>
        </div>
      </div>

      {/* Regenerate Confirmation Modal */}
      {showRegenerateModal && (
        <div className="modal-overlay" onClick={handleRegenerateCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Regenerate Report?</h3>
            <p className="modal-message">
              Are you sure you want to regenerate the comprehensive report? This will create a fresh analysis and replace the current report.
            </p>
            <div className="modal-actions">
              <button
                className="btn-modal-cancel"
                onClick={handleRegenerateCancel}
                disabled={loadingReport}
              >
                Cancel
              </button>
              <button
                className="btn-modal-confirm"
                onClick={handleRegenerateConfirm}
                disabled={loadingReport}
              >
                {loadingReport ? (
                  <>
                    <Loader className="spinner-small" />
                    Regenerating...
                  </>
                ) : (
                  'Yes, Regenerate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Simple EEG Chart Component using SVG with smooth transitions
const EEGChart = ({ data, answerSubmissionIndex = null, previousData = null, isTransitioning = false }) => {
  if (!data || data.length === 0) return null;
  
  // Use React state to track data changes for smooth transitions
  const [displayData, setDisplayData] = React.useState(data);
  const [displayAnswerIndex, setDisplayAnswerIndex] = React.useState(answerSubmissionIndex);
  
  React.useEffect(() => {
    // Smoothly transition to new data
    if (isTransitioning && previousData) {
      // During transition, keep showing previous data briefly
      const timer = setTimeout(() => {
        setDisplayData(data);
        setDisplayAnswerIndex(answerSubmissionIndex);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setDisplayData(data);
      setDisplayAnswerIndex(answerSubmissionIndex);
    }
  }, [data, answerSubmissionIndex, isTransitioning, previousData]);

  const width = 800;
  const height = 300;
  const padding = { top: 20, right: 20, bottom: 40, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Find max value for scaling (use displayData for smooth transitions)
  const maxValue = Math.max(
    ...displayData.flatMap(d => [d.delta, d.theta, d.alpha, d.beta, d.gamma])
  );

  const scaleX = (index) => padding.left + (index / (displayData.length - 1 || 1)) * chartWidth;
  const scaleY = (value) => padding.top + chartHeight - (value / maxValue) * chartHeight;
  
  // Calculate X position for answer submission marker
  const answerMarkerX = displayAnswerIndex !== null 
    ? scaleX(displayAnswerIndex) 
    : null;
  
  // For previous marker, we'd need the previous answer index, but for simplicity
  // we'll just fade the current marker in/out during transition

  const colors = {
    delta: '#8B5CF6',    // Purple
    theta: '#3B82F6',    // Blue
    alpha: '#10B981',    // Green
    beta: '#F59E0B',     // Orange
    gamma: '#EF4444'     // Red
  };

  const bands = ['delta', 'theta', 'alpha', 'beta', 'gamma'];

  return (
    <div className="eeg-chart-wrapper">
      <svg width={width} height={height} className="eeg-chart">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(ratio => (
          <line
            key={`grid-${ratio}`}
            x1={padding.left}
            y1={scaleY(maxValue * ratio)}
            x2={width - padding.right}
            y2={scaleY(maxValue * ratio)}
            stroke="#e5e7eb"
            strokeWidth="1"
            strokeDasharray="4,4"
          />
        ))}

        {/* Y-axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map(ratio => (
          <text
            key={`label-${ratio}`}
            x={padding.left - 10}
            y={scaleY(maxValue * ratio) + 5}
            textAnchor="end"
            fontSize="12"
            fill="#6b7280"
          >
            {(maxValue * ratio).toFixed(0)}
          </text>
        ))}

        {/* Draw lines for each frequency band with smooth transitions */}
        {bands.map(band => {
          const points = displayData.map((d, i) => `${scaleX(i)},${scaleY(d[band])}`).join(' ');
          
          // Calculate previous line points if transitioning
          let previousPoints = null;
          if (previousData && isTransitioning) {
            const prevMaxValue = Math.max(...previousData.flatMap(d => [d.delta, d.theta, d.alpha, d.beta, d.gamma]));
            const prevScaleX = (idx) => padding.left + (idx / (previousData.length - 1 || 1)) * chartWidth;
            const prevScaleY = (val) => padding.top + chartHeight - (val / prevMaxValue) * chartHeight;
            previousPoints = previousData.map((d, i) => `${prevScaleX(i)},${prevScaleY(d[band])}`).join(' ');
          }
          
          return (
            <g key={band} className="frequency-band-group">
              {/* Previous data line (fading out) */}
              {previousPoints && (
                <polyline
                  points={previousPoints}
                  fill="none"
                  stroke={colors[band]}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="frequency-line-fade-out"
                />
              )}
              {/* Current data line (fading in) */}
              <polyline
                points={points}
                fill="none"
                stroke={colors[band]}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`frequency-line ${isTransitioning ? 'frequency-line-fade-in' : ''}`}
              />
            </g>
          );
        })}

        {/* Answer Submission Marker */}
        {answerMarkerX !== null && (
          <g className={`answer-marker-group ${isTransitioning ? 'answer-marker-transitioning' : ''}`}>
            {/* Vertical line marking answer submission */}
            <line
              x1={answerMarkerX}
              y1={padding.top}
              x2={answerMarkerX}
              y2={height - padding.bottom}
              stroke="#EF4444"
              strokeWidth="3"
              strokeDasharray="8,4"
              opacity="0.8"
              className="answer-marker-line"
            />
            {/* Marker point at the line */}
            <circle
              cx={answerMarkerX}
              cy={padding.top + 10}
              r="6"
              fill="#EF4444"
              stroke="white"
              strokeWidth="2"
              className="answer-marker-point"
            />
            {/* Label */}
            <text
              x={answerMarkerX}
              y={padding.top - 5}
              textAnchor="middle"
              fontSize="11"
              fontWeight="600"
              fill="#EF4444"
              className="answer-marker-label"
            >
              Answer Submitted
            </text>
          </g>
        )}

        {/* X-axis */}
        <line
          x1={padding.left}
          y1={height - padding.bottom}
          x2={width - padding.right}
          y2={height - padding.bottom}
          stroke="#374151"
          strokeWidth="2"
        />

        {/* Y-axis */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={height - padding.bottom}
          stroke="#374151"
          strokeWidth="2"
        />
      </svg>

      {/* Legend */}
      <div className="chart-legend">
        {bands.map(band => (
          <div key={band} className="legend-item">
            <span className="legend-color" style={{ backgroundColor: colors[band] }}></span>
            <span className="legend-label">{band.charAt(0).toUpperCase() + band.slice(1)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SurveyResults;
